import { devLog } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { User, Clock } from 'lucide-react';
import { EarningsSection } from '../components/dashboard/EarningsSection';
import { SessionsSection } from '../components/dashboard/SessionsSection';
import { QuickActions, StatsSection } from '../components/dashboard/Sidebar';
import { ProfileTab } from '../components/dashboard/ProfileTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user, updateProfile } = useAuth();
  const [specializations, setSpecializations] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions');
  const [profileData, setProfileData] = useState({
    name: '', phone: '',
    profile: { interests: [], therapy_style_preference: [], goals: '', previous_therapy_experience: false, preferred_professional_type: [] }
  });

  const isProfessional = user?.role === 'professional';

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions`, { headers: getAuthHeaders(), withCredentials: true });
      setSessions(data || []);
    } catch (err) { devLog.error(err); }
    finally { setLoadingSessions(false); }
  }, []);

  const fetchEarnings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/professional/earnings`, { headers: getAuthHeaders(), withCredentials: true });
      setEarnings(data);
    } catch (err) { devLog.error(err); }
  }, []);

  const fetchSpecializations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/specializations`);
      setSpecializations(data);
    } catch (err) { devLog.error(err); }
  }, []);

  useEffect(() => {
    if (user) {
      const p = user.profile || {};
      setProfileData({
        name: user.name || '', phone: user.phone || '',
        profile: {
          interests: p.interests || [], therapy_style_preference: p.therapy_style_preference || [],
          goals: p.goals || '', previous_therapy_experience: p.previous_therapy_experience || false,
          preferred_professional_type: p.preferred_professional_type || []
        }
      });
      fetchSessions();
      if (isProfessional) fetchEarnings();
    }
    fetchSpecializations();
  }, [user, isProfessional, fetchSessions, fetchEarnings, fetchSpecializations]);

  const handleSave = async () => {
    setSaving(true);
    const result = await updateProfile(profileData);
    setSaving(false);
    if (result.success) setEditing(false);
  };

  const toggleInterest = (interest) => {
    setProfileData(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        interests: (prev.profile?.interests || []).includes(interest)
          ? prev.profile.interests.filter(i => i !== interest) : [...(prev.profile?.interests || []), interest]
      }
    }));
  };

  const toggleProfType = (type) => {
    setProfileData(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        preferred_professional_type: (prev.profile?.preferred_professional_type || []).includes(type)
          ? prev.profile.preferred_professional_type.filter(t => t !== type) : [...(prev.profile?.preferred_professional_type || []), type]
      }
    }));
  };

  const allSpecs = Object.values(specializations).flat();
  const upcomingSessions = sessions.filter(s => ['scheduled', 'in_progress', 'pending_payment'].includes(s.status));
  const pastSessions = sessions.filter(s => ['completed', 'cancelled'].includes(s.status));

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="dashboard-page">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-light text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Olá, {user?.name || 'Usuário'}
            </h1>
            <p className="text-[#63635E]">
              {isProfessional ? 'Gerencie suas sessões e ganhos' : 'Gerencie seu perfil e sessões'}
            </p>
          </div>

          {isProfessional && <EarningsSection earnings={earnings} />}

          {/* Tabs */}
          <div className="flex gap-1 mb-8 p-1 bg-[#EAE8E3] rounded-xl w-fit" data-testid="dashboard-tabs">
            {[
              { id: 'sessions', label: 'Sessões', icon: Clock },
              { id: 'profile', label: 'Perfil', icon: User }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id ? 'bg-white text-[#292927] shadow-sm' : 'text-[#63635E] hover:text-[#292927]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 inline-block mr-1.5" strokeWidth={1.5} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'sessions' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <SessionsSection
                  loading={loadingSessions}
                  upcomingSessions={upcomingSessions}
                  pastSessions={pastSessions}
                  isProfessional={isProfessional}
                  onSessionCancelled={(id) => setSessions(prev => prev.map(s => s.session_id === id ? { ...s, status: 'cancelled' } : s))}
                />
              </div>
              <div className="space-y-6">
                <QuickActions isProfessional={isProfessional} />
                <StatsSection sessions={sessions} upcomingCount={upcomingSessions.length} />
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <ProfileTab
              user={user}
              editing={editing}
              saving={saving}
              profileData={profileData}
              isProfessional={isProfessional}
              allSpecializations={allSpecs}
              onSetEditing={setEditing}
              onSave={handleSave}
              onSetProfileData={setProfileData}
              onToggleInterest={toggleInterest}
              onToggleProfType={toggleProfType}
            />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
